import { redirect } from '@tanstack/react-router';

import { queryClient, queryKeys } from '@web/core/query';
import { meRepository } from '@web/data/repositories';
import type { Permission } from '@voll-intelligence/voll-authz';

/**
 * Shared guards. All redirect silently to `/orgs/$orgSlug/home` when the
 * caller lacks the required permission. The project detail query is the
 * source of truth for RES-scoped perms; the org permissions query for
 * ORG-scoped ones. Both caches are primed (and reused) by
 * `useProjectAuthz` and `useAuthz`.
 *
 * When the project fetch itself 403s (Guest hitting a hand-crafted URL),
 * we treat it as "no access" and redirect — letting the error propagate
 * would surface the router's generic error boundary, which is not what
 * the spec wants.
 */
export function redirectToOrgHome(orgSlug: string): never {
  return redirect({ throw: true, to: '/orgs/$orgSlug/overview', params: { orgSlug } }) as never;
}

export async function resolveOrgFromSlug(orgSlug: string): Promise<{ orgId: string }> {
  const me = await queryClient.ensureQueryData({
    queryKey: queryKeys.me.all,
    queryFn: () => meRepository.getMe(),
  });
  const org = me.orgs.find((o) => o.slug === orgSlug);
  if (!org) return redirect({ throw: true, to: '/' }) as never;
  return { orgId: org.id };
}

export async function requireOrgPermission(orgSlug: string, perm: Permission): Promise<void> {
  const { orgId } = await resolveOrgFromSlug(orgSlug);
  const perms = await queryClient.ensureQueryData({
    queryKey: queryKeys.me.permissions(orgId),
    queryFn: () => meRepository.getMyPermissions(orgId),
  });
  if (!perms.permissions.includes(perm)) {
    redirectToOrgHome(orgSlug);
  }
}
